import { Injectable } from '@angular/core';
import { OrderformOrderState, PAYMENT_ERROR_MESSAGES } from '../../../ngrx/orderform.models';

@Injectable({
  providedIn: 'root',
})
export class CustomErrorMessageService {
  public getCustomErrorMessageFromOrderData(result: any, orderData: OrderformOrderState): string {
    const paymentErrorMessagesMapping = orderData?.order?.payment_provider?.payment_error_messages_mapping;
    if (!paymentErrorMessagesMapping) {
      return this._getDefaultOrOriginalMessage(result);
    }

    const errorCode = this._extractErrorCode(result);
    if (!errorCode) {
      return this._getDefaultOrOriginalMessage(result);
    }

    if (paymentErrorMessagesMapping.hasOwnProperty(errorCode)) {
      const paymentErrorIdentifier = paymentErrorMessagesMapping[errorCode];
      const improvedErrorMessage = this._translateErrorMessage(paymentErrorIdentifier);

      if (improvedErrorMessage && improvedErrorMessage.trim() !== '') {
        return improvedErrorMessage;
      }
    }

    return this._getDefaultOrOriginalMessage(result);
  }

  private _extractErrorCode(result: any): string | null {
    if (!result || !result.error) {
      return null;
    }

    const possibleCodeProps = [
      'decline_code', // Stripe decline code
      'code',
      'error_code',
      'errorCode',
    ];

    for (const prop of possibleCodeProps) {
      if (result.error[prop]) {
        return result.error[prop];
      }
    }

    if (typeof result.error === 'string' || typeof result.error === 'number') {
      return result.error.toString();
    }

    return null;
  }

  private _getDefaultOrOriginalMessage(result: any): string {
    if (result?.error?.message) {
      return result.error.message;
    }

    return $localize`:@@payment_method.error.generic:Die Zahlung konnte jetzt nicht verarbeitet werden. Bitte probieren es mit einer anderen Bezahlweise nochmal.`;
  }

  private _translateErrorMessage(paymentErrorIdentifier: string) {
    return (
      PAYMENT_ERROR_MESSAGES[paymentErrorIdentifier] ||
      $localize`:@@payment_method.error.generic:Die Zahlung konnte jetzt nicht verarbeitet werden. Bitte probieren es mit einer anderen Bezahlweise nochmal.`
    );
  }

  public getCustomErrorMessageFromPrimer(error: string): string {
    switch (error) {
      case 'ERROR':
      case 'INVALID_CARD_NUMBER':
      case 'UNKNOWN':
      case 'ISSUER_TEMPORARILY_UNAVAILABLE':
      case 'WITHDRAWAL_LIMIT_EXCEEDED':
        return $localize`:@@payment_method_error.generic_decline:Der Kartenaussteller hat die Zahlung abgelehnt. Bitte kontaktieren Sie Ihre Bank oder versuchen Sie eine andere Zahlungsmethode.`;

      case 'DO_NOT_HONOR':
        return $localize`:@@payment_method_error.do_not_honor:Der Kartenaussteller hat die Zahlung abgelehnt. Bitte kontaktieren Sie Ihre Bank oder versuchen Sie eine andere Zahlungsmethode.`;

      case 'EXPIRED_CARD':
        return $localize`:@@payment_method_error.expired_card:Die Karte ist abgelaufen. Bitte aktualisieren Sie Ihre Daten oder verwenden Sie eine andere Zahlungsmethode.`;

      case 'LOST_OR_STOLEN_CARD':
        return $localize`:@@payment_method_error.card_stolen:Karte als verloren oder gestohlen gemeldet. Bitte kontaktieren Sie Ihre Bank oder verwenden Sie eine andere Zahlungsmethode.`;

      case 'SUSPECTED_FRAUD':
        return $localize`:@@payment_method_error.fraud_suspected:Die Bank blockierte die Abbuchung, da sie ungewöhnlich erschien. Bitte kontaktieren Sie Ihre Bank oder versuchen Sie eine andere Zahlungsmethode.`;

      case 'DECLINED':
        return $localize`:@@payment_method_error.generic_decline:Der Kartenaussteller hat die Zahlung abgelehnt. Bitte kontaktieren Sie Ihre Bank oder versuchen Sie eine andere Zahlungsmethode.`;

      case 'REFER_TO_CARD_ISSUER':
        return $localize`:@@payment_method_error.call_issuer:Der Kartenaussteller hat die Zahlung abgelehnt. Bitte kontaktieren Sie Ihre Bank oder versuchen Sie eine andere Zahlungsmethode.`;

      case 'INSUFFICIENT_FUNDS':
        return $localize`:@@payment_method_error.insufficient_funds:Unzureichende Deckung. Bitte prüfen Sie den Kontostand oder versuchen Sie eine andere Zahlungsmethode.`;

      case 'AUTHENTICATION_REQUIRED':
        return $localize`:@@payment_method_error.authentication_required:Die Bank muss überprüfen, ob Sie es sind. Bitte befolgen Sie die dortigen Schritte zur Bestätigung oder versuchen Sie eine andere Methode.`;

      default:
        // Fall back to the default error message
        return $localize`:@@payment_method_error.generic_decline:Der Kartenaussteller hat die Zahlung abgelehnt. Bitte kontaktieren Sie Ihre Bank oder versuchen Sie eine andere Zahlungsmethode.`;
    }
  }
}
